import { useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  getFormTemplates,
  requestDuplicateFormTemplate,
  requestFormTemplateDelete,
  requestRenameFormTemplate,
} from '@atom/actions/formTemplateActions';
import { retrieveInventorySchemas } from '@atom/actions/inventorySchemaActions';
import ListViewTable from '@atom/components/common/listViewTable/ListViewTable';
import RenameModal from '@atom/components/common/RenameModal';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, Menu, Modal, Progress } from '@atom/mui';
import {
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
  LockIcon,
} from '@atom/mui/icons';
import { inventorySchemasSelector } from '@atom/selectors/inventorySchemasSelectors';
import colors from '@atom/styles/colors';
import layout from '@atom/styles/layout';
import { FormTemplateInfo } from '@atom/types/form';
import { InventorySchemaItem } from '@atom/types/schema';
import { InventorySchemasState, RootState } from '@atom/types/store';
import {
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import {
  FormRepositoryState,
  formRepositoryStateReducer,
  initialState,
  ModalType,
} from './FormRepositoryState';
import RepositoryHeader from './RepositoryHeader';

import leftPaneClasses from '../../styles/left-pane.module.css';
import tableClasses from '../../styles/table.module.css';

const { MenuItem } = Menu;

const styles = {
  progress: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: `calc(100vw - ${layout.sidePaneWidth})`,
    height: `calc(100vh - ${layout.fixedHeaderOffset})`,
  },
};

const displayHeaders = [
  'Form Title',
  'Status',
  'In Use',
  'Inventory Type',
  'Created By',
  'Created On',
  '',
];
const fields = [
  'name',
  'status',
  'inUse',
  'assetType',
  'createdBy',
  'createdDate',
  'additionalOptions',
];

const FormRepository = () => {
  const [, { navigate }] = useNavigation();
  const dispatch = useDispatch();
  const [formRepositoryState, dispatchState] = useReducer(
    formRepositoryStateReducer,
    initialState,
  );
  const { page, limit, openModal }: FormRepositoryState = formRepositoryState;

  const inventorySchemas: InventorySchemasState = useSelector(
    (state: RootState) => inventorySchemasSelector(state),
  );
  const formTemplates: FormTemplateInfo[] = useSelector(
    (state: RootState) => state.formTemplates,
  );
  const formTemplatesTotal: number = useSelector(
    (state: RootState) => state.formTemplatesTotal,
  );
  const loading = useSelector(
    (state: RootState) => state.loading.loadingFormTemplates,
  );

  useEffect(() => {
    if (R.isNil(inventorySchemas) || R.isEmpty(inventorySchemas)) {
      dispatch(retrieveInventorySchemas({ rootSchemas: true }));
    }
    dispatch(getFormTemplates({ page, limit }));
  }, []);

  const handleToggleModal = (modalType: ModalType, rowId: any) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { openModal: { type: modalType, id: rowId } },
    });
  };

  const handleIncrementPage = (increment: number) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { page: page + increment },
    });
    dispatch(getFormTemplates({ page: page + increment, limit }));
  };

  const handleUpdateLimit = (value: number) => {
    dispatchState({
      type: 'UPDATE_STATE',
      data: { limit: value, page: 1 },
    });
    dispatch(getFormTemplates({ page: 1, limit: value }));
  };

  const handleNavigateToTemplate = (formId: string, isPublished: boolean) => {
    const preview =
      isPublished || doesNotHaveRolePermissions(ROLE_SETS.MANAGER)
        ? '/preview'
        : '';
    navigate(`/form-builder/${formId}${preview}`);
  };

  const removeFormTemplate = (id: string): boolean => {
    dispatch(requestFormTemplateDelete(id));
    return true;
  };

  const handleDuplicateFormTemplate = (formTemplateId: string) => {
    dispatch(requestDuplicateFormTemplate(formTemplateId));
  };

  const handleRenameFormTemplate = (id: string, name: string) => {
    dispatch(requestRenameFormTemplate({ id, name }));
  };

  const buildFormCell = (
    row: FormTemplateInfo,
    field: string,
    index: number,
  ) => {
    const matchedSchema = inventorySchemas.find(
      (schema: InventorySchemaItem): boolean => schema.id === row.schemaId,
    );

    const rootSchema = matchedSchema ? matchedSchema : {};

    // @ts-ignore
    const assetType = row.schemaId ? rootSchema.name : 'All';

    const status = row.isPublished
      ? { icon: LockIcon, text: 'Published' }
      : { icon: EditIcon, text: 'Draft' };

    const firstName = R.pathOr('', ['firstName'], row.createdBy);
    const lastName = R.pathOr('', ['lastName'], row.createdBy);

    const isModalOpen = (type: ModalType) => {
      return openModal.type === type && openModal.id === row.id;
    };

    const onConfirmDeleteModal = () => {
      removeFormTemplate(row.id);
      handleToggleModal(ModalType.NONE, null);
    };

    const fieldTemplates = {
      name: (
        <td
          key={index}
          onClick={(): void =>
            handleNavigateToTemplate(row.id, row.isPublished)
          }
          className={tableClasses.clickable}
        >
          {row.name}
        </td>
      ),
      status: (
        <td key={index}>
          <div className={tableClasses.statusCell}>
            <Icon color={colors.neutral.gray} icon={status.icon} />
            <div className={tableClasses.statusText}>{status.text}</div>
          </div>
        </td>
      ),
      inUse: <td key={index}>{row.instanceCount}</td>,
      assetType: <td key={index}>{assetType}</td>,
      createdBy: <td key={index}>{`${firstName} ${lastName}`}</td>,
      createdDate: <td key={index}>{setDisplayDate(row.createdDate)}</td>,
      additionalOptions: (
        <td key={index} className={tableClasses.additionalOptions}>
          {hasRolePermissions(ROLE_SETS.MANAGER) && (
            <>
              <Menu>
                <MenuItem
                  startAdornment={
                    <Icon>
                      <img src={renameIcon} />
                    </Icon>
                  }
                  onClick={() => handleToggleModal(ModalType.RENAME, row.id)}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  startAdornment={<Icon icon={ContentCopyIcon} />}
                  onClick={() => handleDuplicateFormTemplate(row.id)}
                >
                  Duplicate
                </MenuItem>
                {!row.instanceCount && (
                  <MenuItem
                    startAdornment={<Icon icon={DeleteIcon} />}
                    onClick={() => handleToggleModal(ModalType.DELETE, row.id)}
                  >
                    Delete
                  </MenuItem>
                )}
              </Menu>
              <RenameModal
                open={isModalOpen(ModalType.RENAME)}
                closeModal={() => handleToggleModal(ModalType.NONE, null)}
                id={row.id}
                name={row.name}
                type="form Template"
                renameAction={handleRenameFormTemplate}
              />
              {!row.instanceCount && (
                <Modal
                  open={isModalOpen(ModalType.DELETE)}
                  title="Delete Form Template"
                  onCancel={() => handleToggleModal(ModalType.NONE, null)}
                  confirmButtonText="Delete"
                  ConfirmButtonProps={{
                    sx: { background: colors.brand.red },
                  }}
                  onConfirm={onConfirmDeleteModal}
                >
                  Are you sure you want to delete this form template?
                </Modal>
              )}
            </>
          )}
        </td>
      ),
    };
    return fieldTemplates[field] || <td key={index}>{row[field]}</td>;
  };

  const mainPaneContent = loading ? (
    <div>
      <Progress style={styles.progress} />
    </div>
  ) : (
    <ListViewTable
      displayHeaders={displayHeaders}
      dataRows={formTemplates}
      fields={fields}
      // @ts-ignore
      buildCell={buildFormCell}
      totalRows={formTemplatesTotal}
      // @ts-ignore
      incrementPage={handleIncrementPage}
      // @ts-ignore
      updateLimit={handleUpdateLimit}
      page={page}
      limit={limit}
    />
  );

  return (
    <div>
      <RepositoryHeader />
      <div className={leftPaneClasses.leftPane} />
      {mainPaneContent}
    </div>
  );
};

export default FormRepository;
